import type { ReactNode } from "react";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  ScrollRestoration,
} from "react-router-dom";
import {
  Boxes,
  Database,
  Sparkles,
} from "lucide-react";

import { MarketingLayout } from "@/app/marketing/Layout";
import { Home } from "@/app/marketing/Home";
import { Pricing } from "@/app/marketing/Pricing";
import {
  IntegrationDetail,
  IntegrationsPage,
} from "@/app/marketing/Integrations";

import { DocsLayout } from "@/app/docs/Layout";
import { DocPage } from "@/app/docs/DocPage";

import { SignIn } from "@/app/auth/SignIn";
import { SignUp } from "@/app/auth/SignUp";
import { Waitlist } from "@/app/auth/Waitlist";
import { isPreviewMode } from "@/lib/preview-mode";

// Wraps SignIn/SignUp behind the waitlist gate. Bypass with
// `?preview=<anything>` on the URL (persisted in sessionStorage).
function PreviewGate({ children }: { children: ReactNode }) {
  return isPreviewMode() ? <>{children}</> : <Waitlist />;
}

import { ConsoleLayout } from "@/app/console/ConsoleLayout";
import { IntegrationsPage as ConsoleIntegrationsPage } from "@/app/console/Integrations";
import { IntegrationDetailPage as ConsoleIntegrationDetailPage } from "@/app/console/IntegrationDetail";
import { CredentialsPage } from "@/app/console/Credentials";
import { OauthAppsPage } from "@/app/console/OauthApps";
import { PlaceholderPage } from "@/app/console/PlaceholderPage";
import { WorkspaceSettingsPage } from "@/app/console/WorkspaceSettings";
import { ScopeProvider } from "@/lib/scope";

function RootLayout() {
  return (
    <>
      <ScrollRestoration getKey={(loc) => loc.pathname} />
      <ScopeProvider>
        <Outlet />
      </ScopeProvider>
    </>
  );
}

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      // Marketing site
      {
        element: <MarketingLayout />,
        children: [
          { path: "/", element: <Home /> },
          { path: "/pricing", element: <Pricing /> },
          { path: "/integrations", element: <IntegrationsPage /> },
          { path: "/integrations/:slug", element: <IntegrationDetail /> },
        ],
      },
      // Docs
      {
        path: "/docs",
        element: <DocsLayout />,
        children: [
          { index: true, element: <DocPage /> },
          { path: "*", element: <DocPage /> },
        ],
      },
      // Auth (full-bleed, no marketing chrome). Wrapped in PreviewGate
      // so the default view is the waitlist screen; the real auth
      // forms only render when `?preview=<anything>` was used.
      { path: "/login", element: <PreviewGate><SignIn /></PreviewGate> },
      { path: "/signup", element: <PreviewGate><SignUp /></PreviewGate> },
      // Console (post-login). Project selection lives in the sidebar
      // (top); workspace selection lives at the sidebar bottom. There
      // is no Projects page — `/` redirects into Integrations as the
      // default landing.
      {
        path: "/console",
        element: <ConsoleLayout />,
        children: [
          { index: true, element: <Navigate to="/console/integrations" replace /> },
          { path: "integrations", element: <ConsoleIntegrationsPage /> },
          { path: "integrations/:slug", element: <ConsoleIntegrationDetailPage /> },
          {
            path: "loadouts",
            element: (
              <PlaceholderPage
                title="Loadouts"
                description="Named groups of integrations + tools exposed to a specific agent."
                icon={<Boxes className="h-6 w-6" />}
              />
            ),
          },
          {
            path: "memory",
            element: (
              <PlaceholderPage
                title="Memory"
                description="User, project, session, and shared scopes."
                icon={<Sparkles className="h-6 w-6" />}
              />
            ),
          },
          {
            path: "vectors",
            element: (
              <PlaceholderPage
                title="Vectors"
                description="Stores + indexes for RAG. Pluggable backends (pgvector default)."
                icon={<Database className="h-6 w-6" />}
              />
            ),
          },
          { path: "credentials", element: <CredentialsPage /> },
          { path: "oauth-apps", element: <OauthAppsPage /> },
          { path: "settings", element: <WorkspaceSettingsPage /> },
        ],
      },
    ],
  },
]);

export function App() {
  return <RouterProvider router={router} />;
}
